import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { 
  Eye, User, Phone, MapPin, Calendar, Search, 
  Loader2, ChevronDown, ArrowLeft, Download, RefreshCw, Trash2, FileSpreadsheet, MessageSquare, Navigation, Users, TrendingUp, Globe, UserPlus, CheckCircle, X, Building, Briefcase, Mail
} from 'lucide-react';
import * as XLSX from 'xlsx';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Visitors = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [visitors, setVisitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCard, setSelectedCard] = useState('all');
  const [userCards, setUserCards] = useState([]);
  const [loadingCards, setLoadingCards] = useState(true);
  const [timeFilter, setTimeFilter] = useState('all');
  const [deletingId, setDeletingId] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  // CRM Modal State
  const [showCRMModal, setShowCRMModal] = useState(false);
  const [selectedVisitor, setSelectedVisitor] = useState(null);
  const [crmFormData, setCrmFormData] = useState({
    name: '',
    phone: '',
    email: '',
    company: '',
    title: '',
    notes: '',
    location: ''
  });
  const [addingToCRM, setAddingToCRM] = useState(false);

  // Fetch user's cards first
  useEffect(() => {
    if (user) {
      fetchUserCards();
    }
  }, [user]);

  // Fetch visitors when card is selected
  useEffect(() => {
    if (selectedCard === 'all') {
      fetchAllVisitors();
    } else if (selectedCard && userCards.length > 0) {
      fetchVisitors(selectedCard);
    }
  }, [selectedCard, userCards]);

  const fetchAllVisitors = async () => {
    try {
      setLoading(true);
      const token = await user.getIdToken();
      
      const response = await fetch(`${API_URL}/api/analytics/user/visitors`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setVisitors(data.visitors || []);
      } else {
        const error = await response.json();
        toast({ title: 'Error', description: error.detail || 'Failed to fetch visitors', variant: 'destructive' });
      }
    } catch (e) {
      console.error('Error fetching all visitors:', e);
      toast({ title: 'Error', description: 'Failed to fetch visitors', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const fetchUserCards = async () => {
    try {
      setLoadingCards(true);
      const token = await user.getIdToken();
      const response = await fetch(`${API_URL}/api/cards/user/${user.uid}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        const cards = Array.isArray(data) ? data : [data];
        setUserCards(cards.filter(c => c && c._id));
      }
    } catch (e) {
      console.error('Error fetching cards:', e);
    } finally {
      setLoadingCards(false);
    }
  };

  const fetchVisitors = async (cardId) => {
    try {
      setLoading(true);
      const token = await user.getIdToken();
      const response = await fetch(`${API_URL}/api/analytics/card/${cardId}/visitors`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setVisitors(data.visitors || []);
      } else {
        const error = await response.json();
        toast({ title: 'Error', description: error.detail || 'Failed to fetch visitors', variant: 'destructive' });
      }
    } catch (e) {
      console.error('Error fetching visitors:', e);
      toast({ title: 'Error', description: 'Failed to fetch visitors', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  // Filter visitors
  const filteredVisitors = visitors.filter(v => {
    if (timeFilter !== 'all') {
      const date = new Date(v.registeredAt);
      const now = new Date();
      
      if (timeFilter === 'today') {
        if (date.toDateString() !== now.toDateString()) return false;
      } else if (timeFilter === 'week') {
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        if (date < weekAgo) return false;
      } else if (timeFilter === 'month') {
        const monthAgo = new Date();
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        if (date < monthAgo) return false;
      }
    }
    
    const query = searchQuery.toLowerCase();
    if (!query) return true;
    
    return (
      v.name?.toLowerCase().includes(query) ||
      v.phone?.includes(query) ||
      v.phoneFormatted?.includes(query) ||
      v.city?.toLowerCase().includes(query) ||
      v.address?.toLowerCase().includes(query)
    );
  });

  // Format date - shorter for mobile
  const formatDate = (dateStr, short = false) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    if (short) {
      return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
    }
    return date.toLocaleDateString('en-IN', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Export functions
  const exportToCSV = () => {
    if (filteredVisitors.length === 0) {
      toast({ title: 'No data', description: 'No visitors to export', variant: 'destructive' });
      return;
    }

    const headers = selectedCard === 'all' 
      ? ['Name', 'Phone', 'Address', 'Notes', 'Card', 'Date']
      : ['Name', 'Phone', 'Address', 'Notes', 'Date'];
    
    const rows = filteredVisitors.map(v => {
      const baseRow = [
        v.name || '',
        v.phoneFormatted || v.phone || '',
        v.address || `${v.city || ''}, ${v.region || ''}`.replace(/^, |, $/g, '') || '',
        v.notes || '',
      ];
      if (selectedCard === 'all') {
        baseRow.push(v.cardName || '');
      }
      baseRow.push(formatDate(v.registeredAt));
      return baseRow;
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `visitors_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    
    toast({ title: 'Exported', description: `${filteredVisitors.length} visitors exported` });
  };

  const exportToExcel = () => {
    if (filteredVisitors.length === 0) {
      toast({ title: 'No data', description: 'No visitors to export', variant: 'destructive' });
      return;
    }

    const excelData = filteredVisitors.map(v => {
      const row = {
        'Name': v.name || '',
        'Phone': v.phoneFormatted || v.phone || '',
        'Address': v.address || `${v.city || ''}, ${v.region || ''}`.replace(/^, |, $/g, '') || '',
        'Notes': v.notes || '',
      };
      if (selectedCard === 'all') {
        row['Card'] = v.cardName || '';
      }
      row['Date'] = formatDate(v.registeredAt);
      return row;
    });

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(excelData);
    ws['!cols'] = [
      { wch: 20 }, { wch: 18 }, { wch: 30 }, { wch: 40 },
      ...(selectedCard === 'all' ? [{ wch: 15 }] : []),
      { wch: 20 },
    ];
    
    XLSX.utils.book_append_sheet(wb, ws, 'Visitors');
    XLSX.writeFile(wb, `visitors_${new Date().toISOString().split('T')[0]}.xlsx`);
    
    toast({ title: 'Exported', description: `${filteredVisitors.length} visitors exported` });
  };

  // Delete visitor
  const handleDeleteVisitor = async (visitorId, visitorName) => {
    if (!window.confirm(`Delete visitor "${visitorName}"?`)) return;
    
    try {
      setDeletingId(visitorId);
      const token = await user.getIdToken();
      const response = await fetch(`${API_URL}/api/analytics/visitor/${visitorId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        setVisitors(prev => prev.filter(v => v.id !== visitorId));
        toast({ title: 'Deleted', description: 'Visitor removed' });
      } else {
        const error = await response.json();
        toast({ title: 'Error', description: error.detail || 'Failed to delete', variant: 'destructive' });
      }
    } catch (e) {
      toast({ title: 'Error', description: 'Failed to delete', variant: 'destructive' });
    } finally {
      setDeletingId(null);
    }
  };

  // Open CRM modal with visitor data
  const openCRMModal = (visitor) => {
    setSelectedVisitor(visitor);
    setCrmFormData({
      name: visitor.name || '',
      phone: visitor.phoneFormatted || visitor.phone || '',
      email: '',
      company: '',
      title: '',
      notes: visitor.notes || '',
      location: visitor.address || `${visitor.city || ''}${visitor.region ? ', ' + visitor.region : ''}`.trim()
    });
    setShowCRMModal(true);
  };

  // Add visitor to CRM with form data
  const handleAddToCRM = async () => {
    if (!selectedVisitor) return;
    
    try {
      setAddingToCRM(true);
      const token = await user.getIdToken();
      const response = await fetch(`${API_URL}/api/analytics/visitor/${selectedVisitor.id}/add-to-crm`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...crmFormData,
          visitorId: selectedVisitor.id
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        // Update visitor in list to show it's been added
        setVisitors(prev => prev.map(v => 
          v.id === selectedVisitor.id ? { ...v, addedToCRM: true, crmContactId: data.contactId } : v
        ));
        toast({ title: 'Added to Contacts', description: `${crmFormData.name} added to your contacts` });
        setShowCRMModal(false);
        setSelectedVisitor(null);
      } else {
        toast({ 
          title: data.success === false ? 'Already Exists' : 'Error', 
          description: data.message || 'Failed to add contact', 
          variant: data.success === false ? 'default' : 'destructive' 
        });
      }
    } catch (e) {
      toast({ title: 'Error', description: 'Failed to add contact', variant: 'destructive' });
    } finally {
      setAddingToCRM(false);
    }
  };

  // Stats calculations
  const todayCount = visitors.filter(v => {
    const date = new Date(v.registeredAt);
    return date.toDateString() === new Date().toDateString();
  }).length;

  const weekCount = visitors.filter(v => {
    const date = new Date(v.registeredAt);
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    return date >= weekAgo;
  }).length;

  const citiesCount = new Set(visitors.map(v => v.city).filter(Boolean)).size;

  if (!user) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="text-center">
            <div className="w-20 h-20 bg-violet-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Eye className="w-10 h-10 text-violet-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Sign in to view leads</h2>
            <p className="text-gray-500 mb-6 text-sm">Track who's viewing your digital business cards</p>
            <Button onClick={() => navigate('/sign-in')} className="w-full max-w-xs">
              Sign In
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50">
        {/* Sticky Header */}
        <div className="sticky top-14 sm:top-16 z-40 bg-white border-b border-gray-200 shadow-sm">
          <div className="px-3 sm:px-6 py-3">
            {/* Title Row */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-violet-100 rounded-lg flex items-center justify-center">
                  <Eye className="w-4 h-4 text-violet-600" />
                </div>
                <div>
                  <h1 className="text-base sm:text-lg font-bold text-gray-900">Leads</h1>
                  <p className="text-xs text-gray-500 hidden sm:block">People who scanned your card</p>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => selectedCard === 'all' ? fetchAllVisitors() : fetchVisitors(selectedCard)}
                  disabled={loading}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Refresh"
                >
                  <RefreshCw className={`w-5 h-5 text-gray-600 ${loading ? 'animate-spin' : ''}`} />
                </button>
                
                {/* Export Menu */}
                <div className="relative">
                  <select
                    onChange={(e) => {
                      if (e.target.value === 'excel') exportToExcel();
                      else if (e.target.value === 'csv') exportToCSV();
                      e.target.value = '';
                    }}
                    disabled={filteredVisitors.length === 0}
                    className="appearance-none bg-violet-600 text-white text-sm font-medium px-3 py-2 pr-8 rounded-lg cursor-pointer disabled:opacity-50"
                    defaultValue=""
                  >
                    <option value="" disabled>Export</option>
                    <option value="excel">Excel</option>
                    <option value="csv">CSV</option>
                  </select>
                  <Download className="w-4 h-4 text-white absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>
              </div>
            </div>

            {/* Search Bar */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search name, phone, location..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-4 h-10 text-sm bg-gray-50 border-gray-200"
                data-testid="visitor-search-input"
              />
            </div>

            {/* Filter Pills */}
            <div className="flex items-center gap-2 mt-3 overflow-x-auto pb-1 -mx-3 px-3 scrollbar-hide">
              {/* Card Filter */}
              {userCards.length > 0 && (
                <select
                  value={selectedCard}
                  onChange={(e) => setSelectedCard(e.target.value)}
                  className="flex-shrink-0 text-xs font-medium px-3 py-1.5 rounded-full border border-gray-200 bg-white"
                  data-testid="card-selector"
                >
                  <option value="all">All Cards</option>
                  {userCards.map((card) => (
                    <option key={card._id} value={card._id}>
                      {card.name || card.username || 'My Card'}
                    </option>
                  ))}
                </select>
              )}
              
              {/* Time Filter Pills */}
              {['all', 'today', 'week', 'month'].map((filter) => (
                <button
                  key={filter}
                  onClick={() => setTimeFilter(filter)}
                  className={`flex-shrink-0 text-xs font-medium px-3 py-1.5 rounded-full transition-colors ${
                    timeFilter === filter
                      ? 'bg-violet-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {filter === 'all' ? 'All Time' : filter === 'today' ? 'Today' : filter === 'week' ? 'This Week' : 'This Month'}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="px-3 sm:px-6 py-4 max-w-4xl mx-auto">
          {/* Stats Grid - Compact on Mobile */}
          <div className="grid grid-cols-4 gap-2 sm:gap-4 mb-4">
            <div className="bg-white rounded-xl p-3 text-center shadow-sm border border-gray-100">
              <div className="flex items-center justify-center w-8 h-8 bg-violet-100 rounded-lg mx-auto mb-1">
                <Users className="w-4 h-4 text-violet-600" />
              </div>
              <p className="text-lg sm:text-2xl font-bold text-violet-600">{visitors.length}</p>
              <p className="text-[10px] sm:text-xs text-gray-500">Total</p>
            </div>
            <div className="bg-white rounded-xl p-3 text-center shadow-sm border border-gray-100">
              <div className="flex items-center justify-center w-8 h-8 bg-green-100 rounded-lg mx-auto mb-1">
                <TrendingUp className="w-4 h-4 text-green-600" />
              </div>
              <p className="text-lg sm:text-2xl font-bold text-green-600">{todayCount}</p>
              <p className="text-[10px] sm:text-xs text-gray-500">Today</p>
            </div>
            <div className="bg-white rounded-xl p-3 text-center shadow-sm border border-gray-100">
              <div className="flex items-center justify-center w-8 h-8 bg-blue-100 rounded-lg mx-auto mb-1">
                <Calendar className="w-4 h-4 text-blue-600" />
              </div>
              <p className="text-lg sm:text-2xl font-bold text-blue-600">{weekCount}</p>
              <p className="text-[10px] sm:text-xs text-gray-500">Week</p>
            </div>
            <div className="bg-white rounded-xl p-3 text-center shadow-sm border border-gray-100">
              <div className="flex items-center justify-center w-8 h-8 bg-amber-100 rounded-lg mx-auto mb-1">
                <Globe className="w-4 h-4 text-amber-600" />
              </div>
              <p className="text-lg sm:text-2xl font-bold text-amber-600">{citiesCount}</p>
              <p className="text-[10px] sm:text-xs text-gray-500">Cities</p>
            </div>
          </div>

          {/* Results Count */}
          {(timeFilter !== 'all' || searchQuery) && (
            <p className="text-xs text-gray-500 mb-3">
              Showing {filteredVisitors.length} visitor{filteredVisitors.length !== 1 ? 's' : ''}
              {timeFilter !== 'all' && ` from ${timeFilter === 'today' ? 'today' : timeFilter === 'week' ? 'this week' : 'this month'}`}
            </p>
          )}

          {/* Visitors List */}
          {loading || loadingCards ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
            </div>
          ) : filteredVisitors.length === 0 ? (
            <div className="bg-white rounded-2xl p-8 text-center shadow-sm border border-gray-100">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Eye className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-base font-semibold text-gray-900 mb-1">
                {searchQuery ? 'No matching visitors' : 'No visitors yet'}
              </h3>
              <p className="text-sm text-gray-500">
                {searchQuery ? 'Try a different search' : 'Share your card to get visitors'}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredVisitors.map((visitor, idx) => (
                <div 
                  key={visitor.id || idx} 
                  className="bg-white rounded-xl p-3 sm:p-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
                >
                  {/* Mobile Layout */}
                  <div className="flex items-start gap-3">
                    {/* Avatar */}
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-violet-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-sm sm:text-lg flex-shrink-0">
                      {visitor.name?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                    
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      {/* Name & Date Row */}
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="font-semibold text-gray-900 text-sm sm:text-base truncate">
                          {visitor.name || 'Unknown'}
                        </h3>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className="text-[10px] sm:text-xs text-gray-400">
                            {formatDate(visitor.registeredAt, true)}
                          </span>
                          {/* Add to Contact Button - Icon only */}
                          {visitor.addedToCRM ? (
                            <button
                              className="p-1.5 text-green-600 cursor-default"
                              title="Added to Contacts"
                              disabled
                            >
                              <CheckCircle className="w-4 h-4" />
                            </button>
                          ) : (
                            <button
                              onClick={() => openCRMModal(visitor)}
                              className="p-1.5 hover:bg-violet-50 rounded transition-colors"
                              title="Add to Contact"
                            >
                              <UserPlus className="w-4 h-4 text-violet-600 hover:text-violet-700" />
                            </button>
                          )}
                          {/* Delete Button */}
                          <button
                            onClick={() => handleDeleteVisitor(visitor.id, visitor.name)}
                            disabled={deletingId === visitor.id}
                            className="p-1.5 hover:bg-red-50 rounded transition-colors"
                            title="Delete"
                          >
                            {deletingId === visitor.id ? (
                              <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                            ) : (
                              <Trash2 className="w-4 h-4 text-gray-400 hover:text-red-500" />
                            )}
                          </button>
                        </div>
                      </div>
                      
                      {/* Phone */}
                      <a 
                        href={`tel:${visitor.phoneFormatted || visitor.phone}`}
                        className="flex items-center gap-1.5 text-xs sm:text-sm text-gray-600 hover:text-violet-600 mt-1"
                      >
                        <Phone className="w-3.5 h-3.5 text-gray-400" />
                        <span>{visitor.phoneFormatted || visitor.phone || 'N/A'}</span>
                      </a>
                      
                      {/* Location */}
                      {(visitor.address || visitor.city) && (
                        <a
                          href={
                            visitor.latitude && visitor.longitude
                              ? `https://www.google.com/maps?q=${visitor.latitude},${visitor.longitude}`
                              : `https://www.google.com/maps/search/${encodeURIComponent(visitor.address || `${visitor.city || ''} ${visitor.region || ''}`.trim())}`
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1.5 text-xs sm:text-sm text-violet-600 hover:text-violet-800 mt-1"
                        >
                          <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                          <span className="truncate">{visitor.address || `${visitor.city || ''}${visitor.region ? `, ${visitor.region}` : ''}`}</span>
                          <Navigation className="w-3 h-3 flex-shrink-0" />
                        </a>
                      )}
                      
                      {/* Notes */}
                      {visitor.notes && (
                        <div className="flex items-start gap-1.5 mt-2 bg-gray-50 rounded-lg p-2">
                          <MessageSquare className="w-3.5 h-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
                          <p className="text-xs text-gray-600 italic line-clamp-2">"{visitor.notes}"</p>
                        </div>
                      )}
                      
                      {/* Card Name Badge - when showing all cards */}
                      {selectedCard === 'all' && visitor.cardName && (
                        <span className="inline-block mt-2 text-[10px] font-medium bg-violet-100 text-violet-700 px-2 py-0.5 rounded-full">
                          {visitor.cardName}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {/* Add to CRM Modal */}
      <Dialog open={showCRMModal} onOpenChange={setShowCRMModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-violet-600" />
              Add to Contact
            </DialogTitle>
            <DialogDescription>
              Review and edit details before adding to your contacts
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Name */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1.5 block flex items-center gap-2">
                <User className="w-4 h-4 text-gray-400" />
                Name <span className="text-red-500">*</span>
              </label>
              <Input
                type="text"
                placeholder="Contact name"
                value={crmFormData.name}
                onChange={(e) => setCrmFormData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full"
              />
            </div>

            {/* Phone */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1.5 block flex items-center gap-2">
                <Phone className="w-4 h-4 text-gray-400" />
                Phone
              </label>
              <Input
                type="tel"
                placeholder="Phone number"
                value={crmFormData.phone}
                onChange={(e) => setCrmFormData(prev => ({ ...prev, phone: e.target.value }))}
                className="w-full"
              />
            </div>

            {/* Email */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1.5 block flex items-center gap-2">
                <Mail className="w-4 h-4 text-gray-400" />
                Email
              </label>
              <Input
                type="email"
                placeholder="Email address"
                value={crmFormData.email}
                onChange={(e) => setCrmFormData(prev => ({ ...prev, email: e.target.value }))}
                className="w-full"
              />
            </div>

            {/* Company & Title Row */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1.5 block flex items-center gap-2">
                  <Building className="w-4 h-4 text-gray-400" />
                  Company
                </label>
                <Input
                  type="text"
                  placeholder="Company"
                  value={crmFormData.company}
                  onChange={(e) => setCrmFormData(prev => ({ ...prev, company: e.target.value }))}
                  className="w-full"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1.5 block flex items-center gap-2">
                  <Briefcase className="w-4 h-4 text-gray-400" />
                  Title
                </label>
                <Input
                  type="text"
                  placeholder="Job title"
                  value={crmFormData.title}
                  onChange={(e) => setCrmFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full"
                />
              </div>
            </div>

            {/* Location */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1.5 block flex items-center gap-2">
                <MapPin className="w-4 h-4 text-gray-400" />
                Location
              </label>
              <Input
                type="text"
                placeholder="Location/Address"
                value={crmFormData.location}
                onChange={(e) => setCrmFormData(prev => ({ ...prev, location: e.target.value }))}
                className="w-full"
              />
            </div>

            {/* Notes */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1.5 block flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-gray-400" />
                Notes
              </label>
              <Textarea
                placeholder="Add notes about this contact..."
                value={crmFormData.notes}
                onChange={(e) => setCrmFormData(prev => ({ ...prev, notes: e.target.value }))}
                className="w-full"
                rows={3}
              />
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowCRMModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddToCRM}
                disabled={addingToCRM || !crmFormData.name.trim()}
                className="flex-1 bg-violet-600 hover:bg-violet-700 text-white"
              >
                {addingToCRM ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Adding...
                  </>
                ) : (
                  <>
                    <UserPlus className="w-4 h-4 mr-2" />
                    Add to Contact
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Visitors;
